import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, CircularProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import messageService from '../../services/messageService';
import { useMessages } from '../../contexts/MessageContext';

const ACCENT = '#ffcc00';

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },

  /* ── TOOLBAR ── */
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    background: '#f9fafb', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
    flexWrap: 'wrap', rowGap: 6,
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', flex: '1 1 180px', minWidth: 0,
    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
    padding: '5px 10px', gap: 6,
  },
  searchInput: {
    border: 'none', outline: 'none', flex: 1, fontSize: 13, background: 'transparent', color: '#111827',
  },
  filterGroup: { display: 'flex', gap: 4 },
  filterBtn: {
    padding: '4px 10px', borderRadius: 5, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 500, color: '#374151',
    transition: 'all 0.12s',
    '&:hover': { background: '#f3f4f6' },
  },
  filterBtnActive: {
    background: 'rgb(28,37,54) !important', color: '#fff !important', borderColor: 'rgb(28,37,54) !important',
  },
  iconBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 5,
    color: '#6b7280', '&:hover': { background: '#f3f4f6' },
  },

  /* ── MESSAGE LIST ── */
  list: { flex: 1, overflowY: 'auto' },

  row: {
    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 16px',
    borderBottom: '1px solid #f0f1f3', cursor: 'pointer', transition: 'background 0.1s',
    '&:hover': { background: '#f9fafb' },
  },
  rowUnread: { background: '#fefce8 !important', '&:hover': { background: '#fef9c3 !important' } },

  dotWrap: { width: 8, paddingTop: 6, flexShrink: 0 },
  dot: { width: 7, height: 7, borderRadius: '50%', background: ACCENT },

  avatar: {
    width: 36, height: 36, borderRadius: '50%', background: 'rgb(28,37,54)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 15, flexShrink: 0,
  },

  rowBody: { flex: 1, minWidth: 0 },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  sender: { fontSize: 13.5, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  senderRead: { fontWeight: 400, color: '#374151' },
  dateText: { fontSize: 11.5, color: '#9ca3af', flexShrink: 0 },
  subject: { fontSize: 13, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 },
  subjectBold: { fontWeight: 600, color: '#111827' },
  preview: { fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2, '@media (max-width:480px)': { display: 'none' } },

  rowRight: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingTop: 2 },
  tag: {
    padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.02em',
  },
  tagInfo:     { background: '#dbeafe', color: '#1d4ed8' },
  tagFeedback: { background: '#dcfce7', color: '#15803d' },
  tagError:    { background: '#fee2e2', color: '#b91c1c' },
  urgentIcon:  { color: '#ef4444', fontSize: 12 },
  replyBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '3px 5px', borderRadius: 4,
    color: '#9ca3af', '&:hover': { color: '#374151', background: '#f3f4f6' },
  },

  /* ── EMPTY / LOADING ── */
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#9ca3af', gap: 12, padding: 48, textAlign: 'center' },
  emptyIcon: { fontSize: 48, color: '#e5e7eb', marginBottom: 4 },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: '#6b7280' },
  emptyDesc: { fontSize: 13, color: '#9ca3af', maxWidth: 260 },

  /* ── PAGINATION ── */
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb',
    fontSize: 12, color: '#6b7280', flexShrink: 0,
  },
  pageBtn: {
    background: 'none', border: '1px solid #d1d5db', borderRadius: 5,
    padding: '3px 10px', cursor: 'pointer', fontSize: 12, color: '#374151',
    '&:disabled': { opacity: 0.4, cursor: 'default' },
    '&:not(:disabled):hover': { background: '#f3f4f6' },
  },
}));

const TYPE_TAGS = {
  INFO:     { label: 'Info',     cls: 'tagInfo' },
  FEEDBACK: { label: 'Feedback', cls: 'tagFeedback' },
  ERROR:    { label: 'Error',    cls: 'tagError' },
};

const PAGE_SIZE = 20;

export default function MessagesInbox({ userId }) {
  const classes = useStyles();
  const history = useHistory();
  const { refreshStats, newMessageTimestamp, markedReadTimestamp, decrementUnread } = useMessages();

  const [messages, setMessages]   = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter]   = useState(null);
  const [readFilter, setReadFilter]   = useState('all');
  const [page, setPage]           = useState(0);

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    messageService.getInboxMessages(userId)
      .then(r => { setMessages(r.data?.items || []); setLoading(false); })
      .catch(() => { toast.error('Failed to load inbox'); setLoading(false); });
  }, [userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (newMessageTimestamp) load(); }, [newMessageTimestamp]); // eslint-disable-line
  useEffect(() => { if (markedReadTimestamp) load(); }, [markedReadTimestamp]); // eslint-disable-line

  const cleanName = (name) => name?.replace(/\s*-\s*Split$/i, '') || 'User';

  useEffect(() => {
    let r = [...messages];
    if (readFilter === 'unread') r = r.filter(m => !m.isRead);
    if (readFilter === 'read')   r = r.filter(m => m.isRead);
    if (typeFilter) r = r.filter(m => m.messageType?.typeCode === typeFilter);
    if (search) {
      const t = search.toLowerCase();
      r = r.filter(m => m.subject?.toLowerCase().includes(t) || m.senderName?.toLowerCase().includes(t) || m.content?.toLowerCase().includes(t));
    }
    setFiltered(r);
    setPage(0);
  }, [messages, search, typeFilter, readFilter]);

  const open = (msg) => {
    if (!msg.isRead) {
      decrementUnread(1);
      messageService.markAsRead(msg.id)
        .then(() => setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isRead: true } : m)))
        .catch(() => {});
    }
    history.push(`/messages/conversation/${msg.senderId}`);
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d), now = new Date();
    if (dt.toDateString() === now.toDateString()) return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const y = new Date(now); y.setDate(now.getDate() - 1);
    if (dt.toDateString() === y.toDateString()) return 'Yesterday';
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const paged = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  return (
    <div className={classes.root}>
      {/* ── TOOLBAR ── */}
      <div className={classes.toolbar}>
        <div className={classes.searchWrap}>
          <i className="fa-solid fa-magnifying-glass" style={{ color: '#9ca3af', fontSize: 13 }} />
          <input
            className={classes.searchInput}
            placeholder="Search messages…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className={classes.iconBtn} onClick={() => setSearch('')} style={{ padding: 0 }}>
              <i className="fa-solid fa-xmark" style={{ fontSize: 13 }} />
            </button>
          )}
        </div>

        <div className={classes.filterGroup}>
          {['all','unread','read'].map(f => (
            <button key={f}
              className={`${classes.filterBtn} ${readFilter === f ? classes.filterBtnActive : ''}`}
              onClick={() => setReadFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className={classes.filterGroup}>
          {[null,'INFO','FEEDBACK','ERROR'].map(t => (
            <button key={t || 'all'}
              className={`${classes.filterBtn} ${typeFilter === t ? classes.filterBtnActive : ''}`}
              onClick={() => setTypeFilter(t)}>
              {t ? TYPE_TAGS[t].label : 'All'}
            </button>
          ))}
        </div>

        <Tooltip title="Refresh">
          <button className={classes.iconBtn} onClick={load} disabled={loading}>
            <i className={`fa-solid fa-rotate-right ${loading ? 'fa-spin' : ''}`} style={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      </div>

      {/* ── LIST ── */}
      <div className={classes.list}>
        {loading && !messages.length ? (
          <div className={classes.empty}><CircularProgress size={32} /></div>
        ) : !filtered.length ? (
          <div className={classes.empty}>
            <i className={`fa-solid fa-inbox ${classes.emptyIcon}`} />
            <span className={classes.emptyTitle}>{search || typeFilter || readFilter !== 'all' ? 'No messages match' : 'Inbox is empty'}</span>
            <span className={classes.emptyDesc}>{search || typeFilter || readFilter !== 'all' ? 'Try adjusting your filters or search term.' : 'New messages will appear here.'}</span>
          </div>
        ) : paged.map(msg => {
          const tag = TYPE_TAGS[msg.messageType?.typeCode];
          return (
            <div key={msg.id}
              className={`${classes.row} ${!msg.isRead ? classes.rowUnread : ''}`}
              onClick={() => open(msg)}>

              <div className={classes.dotWrap}>
                {!msg.isRead && <div className={classes.dot} />}
              </div>

              <div className={classes.avatar}>
                {(cleanName(msg.senderName)?.charAt(0) || 'U').toUpperCase()}
              </div>

              <div className={classes.rowBody}>
                <div className={classes.rowTop}>
                  <span className={`${classes.sender} ${msg.isRead ? classes.senderRead : ''}`}>
                    {cleanName(msg.senderName)}
                  </span>
                  <span className={classes.dateText}>{fmtDate(msg.createdAt)}</span>
                </div>
                <div className={`${classes.subject} ${!msg.isRead ? classes.subjectBold : ''}`}>
                  {msg.priority === 'urgent' && <i className={`fa-solid fa-circle-exclamation ${classes.urgentIcon}`} style={{ marginRight: 4 }} />}
                  {msg.subject}
                </div>
                <div className={classes.preview}>{msg.content?.slice(0, 90)}{msg.content?.length > 90 ? '…' : ''}</div>
              </div>

              <div className={classes.rowRight}>
                {tag && <span className={`${classes.tag} ${classes[tag.cls]}`}>{tag.label}</span>}
                <button
                  className={classes.replyBtn}
                  onClick={e => { e.stopPropagation(); history.push(`/messages/compose?replyTo=${msg.senderId}`); }}
                  title="Reply">
                  <i className="fa-solid fa-reply" style={{ fontSize: 13 }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── PAGINATION ── */}
      {filtered.length > PAGE_SIZE && (
        <div className={classes.pagination}>
          <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={classes.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <button className={classes.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        </div>
      )}
    </div>
  );
}
